import React, { useCallback, useEffect, useRef, useState } from "react";
import "./OnlyOfficeViewer.css";
import { DocumentEditor } from "@onlyoffice/document-editor-react";
import { getDocumentConfig } from "../../../../../config/onlyOfficeConfig";
import api from "../../../../../services/apiInstance/api";
import { debug, debugWarn, debugError } from "../../../../../utils/debug";

const OnlyOfficeViewer = (props) => {
  const {
    documentUrl,
    documentName,
    fileId,
    organizationId,
    entityType,
    onError,
    onReady,
  } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [configState, setConfigState] = useState(null);
  const [onlyOfficeUrl, setOnlyOfficeUrl] = useState(null);
  const [keyUnique, setKeyUnique] = useState();
  // Стабилизируем внешние колбэки, чтобы не триггерить эффект при их изменении
  const onErrorRef = useRef(onError);
  const onReadyRef = useRef(onReady);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  const onDocumentReady = useCallback(() => {
    // const documentEditor = window.DocEditor?.instances?.["docEditor"];
    // if (documentEditor && typeof documentEditor.showMessage === "function") {
    //   documentEditor.showMessage("Welcome to ONLYOFFICE Editor!");
    // }
  }, []);

  const handleLoadComponentError = useCallback(
    (errorCode, errorDescription) => {
      debugError(
        "[OnlyOfficeViewer]",
        "❌ Ошибка загрузки компонента ONLYOFFICE:",
        { errorCode, errorDescription }
      );
      const message = errorDescription || `Код ошибки: ${errorCode}`;
      setError(message);
      setIsLoading(false);
      onErrorRef.current && onErrorRef.current(new Error(message));
    },
    []
  );

  const handleEditorReadyEvent = useCallback(() => {
    onDocumentReady();
    setIsLoading(false);
    onReadyRef.current && onReadyRef.current();
  }, [onDocumentReady]);

  const handleEditorStateChangeEvent = useCallback((event) => {
    debug("[OnlyOfficeViewer]", "==== 📊 Document state changed:", event);
  }, []);

  const handleEditorErrorEvent = useCallback((event) => {
    debugError("[OnlyOfficeViewer]", "==== ❌ ONLYOFFICE error:", event);
    const errorMessage =
      event?.data?.errorDescription ||
      event?.data ||
      event?.message ||
      "Неизвестная ошибка";
    setError(`Ошибка ONLYOFFICE: ${errorMessage}`);
    setIsLoading(false);
    onErrorRef.current && onErrorRef.current(new Error(errorMessage));
  }, []);

  useEffect(() => {
    setKeyUnique(Date.now());
  }, []);

  // Формируем конфиг и, при необходимости, подписываем его на сервере, чтобы получить JWT
  useEffect(() => {
    let isCancelled = false;

    // Логирование входных параметров
    debug("[OnlyOfficeViewer]", "🔍 useEffect запущен:", {
      documentUrl,
      documentName,
      fileId,
      organizationId,
      entityType,
      keyUnique,
      hasAllParams: !!(documentUrl && documentName && fileId && keyUnique),
    });

    if (!documentUrl || !documentName || !fileId || !keyUnique) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setConfigState(null);
    setOnlyOfficeUrl(null);

    // Абсолютный callbackUrl к бэкенду на том же домене, что и UI
    const callbackUrl = new URL(
      "/onlyoffice/callback",
      window.location.origin
    ).toString();
    debug("[OnlyOfficeViewer]", "🔗 Callback URL:", callbackUrl);

    // Базовый конфиг (без токена), прокидываем callbackUrl в editorConfig
    // key=<org_id>_<entity_type>_<fileId>
    const userName =
      (window.APP_DATA && window.APP_DATA.userName) || "Undefined";
    const login = (window.APP_DATA && window.APP_DATA.login) || "Undefined";
    const documentKey = `${organizationId}_${entityType}_${fileId}_${keyUnique}`;

    debug("[OnlyOfficeViewer]", "👤 Пользователь:", {
      userName,
      login,
      documentKey,
    });

    const baseConfig = getDocumentConfig(
      documentUrl,
      documentName,
      userName,
      documentKey,
      {
        editorConfig: {
          callbackUrl,
          user: {
            id: login,
            name: userName,
          },
        },
      }
    );

    debug("[OnlyOfficeViewer]", "📋 Базовый конфиг:", baseConfig);

    // Формируем payload для подписания на сервере (без функций/невалидных полей)
    const signPayload = {
      document: baseConfig.document,
      documentType: baseConfig.documentType,
      editorConfig: baseConfig.editorConfig,
    };

    debug("[OnlyOfficeViewer]", "📤 Отправка запроса на подписание payload...");

    api
      .onlyOfficeSignPayload({ payload: JSON.stringify(signPayload) })
      .then(({ data }) => {
        debug("[OnlyOfficeViewer]", "📥 Ответ от сервера:", data);

        const theOnlyOfficeUrl =
          data?.onlyOfficeUrl || data?.onlyofficeUrl || null;
        const signedToken =
          (data && (data.token || data.jwtToken || data.jwt)) || null;

        if (!theOnlyOfficeUrl) {
          const errorMsg = `Сервер не вернул URL ONLYOFFICE. Полученные данные: ${JSON.stringify(
            data
          )}`;
          debugError("[OnlyOfficeViewer]", "❌", errorMsg);
          throw new Error(errorMsg);
        }
        if (!signedToken) {
          const errorMsg = `Сервер не вернул подписанный JWT. Полученные данные: ${JSON.stringify(
            data
          )}`;
          debugError("[OnlyOfficeViewer]", "❌", errorMsg);
          throw new Error(errorMsg);
        }

        const cfg = {
          ...baseConfig,
          token: signedToken,
          document: { ...baseConfig.document, token: signedToken },
          editorConfig: { ...baseConfig.editorConfig, token: signedToken },
        };

        debug("[OnlyOfficeViewer]", "✅ Конфигурация готова:", {
          onlyOfficeUrl: theOnlyOfficeUrl,
          hasConfig: !!cfg,
          documentKey: cfg?.document?.key,
        });

        if (!isCancelled) {
          setOnlyOfficeUrl(theOnlyOfficeUrl);
          setConfigState(cfg);
          setIsLoading(false); // Прекращаем загрузку после успешного получения данных
          debug(
            "[OnlyOfficeViewer]",
            "🎯 Состояние обновлено: onlyOfficeUrl и configState установлены"
          );
        } else {
          debugWarn(
            "[OnlyOfficeViewer]",
            "⚠️ Компонент размонтирован, состояние не обновлено"
          );
        }
      })
      .catch((e) => {
        debugError(
          "[OnlyOfficeViewer]",
          "❌ Ошибка получения подписанного JWT:",
          {
            message: e.message,
            error: e,
            stack: e.stack,
            response: e.response,
            status: e.response?.status,
            statusText: e.response?.statusText,
            code: e.code,
          }
        );
        if (!isCancelled) {
          setError("Не удалось подписать документ для просмотра в OnlyOffice");
          setIsLoading(false);
        }
      });

    return () => {
      debug("[OnlyOfficeViewer]", "🧹 Очистка эффекта");
      isCancelled = true;
      try {
        if (
          window.DocEditor &&
          typeof window.DocEditor.destroyEditor === "function"
        ) {
          window.DocEditor.destroyEditor("docEditor");
        }
      } catch (_) {}
    };
  }, [
    documentUrl,
    documentName,
    organizationId,
    entityType,
    fileId,
    keyUnique,
  ]);

  // Логирование состояния для отладки
  useEffect(() => {
    debug("[OnlyOfficeViewer]", "📊 Текущее состояние:", {
      isLoading,
      error,
      hasConfigState: !!configState,
      onlyOfficeUrl,
      documentUrl,
      documentName,
    });
  }, [isLoading, error, configState, onlyOfficeUrl, documentUrl, documentName]);

  if (error) {
    return (
      <div className="OnlyOfficeViewer__error">
        <p>Ошибка загрузки документа: {error}</p>
      </div>
    );
  }

  return (
    <div className="OnlyOfficeViewer__container">
      {isLoading && (
        <div className="OnlyOfficeViewer__loading">
          <p>Загрузка документа для предпросмотра...</p>
        </div>
      )}
      {!error && !isLoading && configState && onlyOfficeUrl && (
        <div className="OnlyOfficeViewer__editor">
          <DocumentEditor
            key={configState?.document?.key || `${documentUrl}:${documentName}`}
            id="docEditor"
            documentServerUrl={onlyOfficeUrl}
            config={configState}
            events_onAppReady={handleEditorReadyEvent}
            events_onDocumentStateChange={handleEditorStateChangeEvent}
            events_onError={handleEditorErrorEvent}
            onLoadComponentError={handleLoadComponentError}
          />
        </div>
      )}
    </div>
  );
};

export default React.memo(OnlyOfficeViewer);
